import { useAtom } from 'jotai'
import { useAtomValue } from 'jotai'
import { useQuery } from '@tanstack/react-query'
import { Container, Stack, Title, Text, Paper, Group, Badge, Button, Loader } from '@mantine/core'
import { useNavigate } from 'react-router-dom'
import { IconPlayerPlay, IconSettings, IconChartRadar, IconBrain, IconUsers, IconListDetails, IconMap2 } from '@tabler/icons-react'
import { authUserAtom, isProvisionalAtom } from '../atoms/authAtoms'
import { useAuth } from '../hooks/useAuth'
import { playerSessionService } from '../services/playerSessionService'
import { GlowAvatar } from '../components/common/GlowAvatar'
import { AlertBanner } from '../components/common/AlertBanner'

// Sessions carry the role *key* (e.g. "comms_officer"), not its display label —
// the story's role definitions aren't in the sessions payload. Humanize the key
// for the badge: "comms_officer" → "Comms Officer".
function humanizeRole(roleKey) {
  if (!roleKey) return ''
  return String(roleKey)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

export default function Profile() {
  const [user] = useAtom(authUserAtom)
  const isProvisional = useAtomValue(isProvisionalAtom)
  const { logout } = useAuth()
  const navigate = useNavigate()

  const { data: completedSessions, isLoading: sessionsLoading } = useQuery({
    queryKey: ['completedSessions'],
    queryFn: () => playerSessionService.getCompletedSessions(),
    enabled: !isProvisional,
    staleTime: 60 * 1000,
  })

  return (
    <Container size="sm" py="xl">
      <Stack gap="lg">
        <Title order={2} ff="Orbitron, sans-serif" c="cyan.4">
          Profile
        </Title>

        {isProvisional && (
          <AlertBanner>
            PROVISIONAL ACCOUNT — Upgrade in Settings to secure your progress
          </AlertBanner>
        )}

        <Paper p="lg" className="glass">
          <Group gap="md">
            <GlowAvatar
              name={user?.name || user?.email}
              size={60}
              active
            />
            <Stack gap={4}>
              <Text fw={600} size="lg">
                {user?.name || 'Explorer'}
              </Text>
              <Text size="sm" c="dimmed">
                {user?.email}
              </Text>
              {isProvisional && (
                <Badge size="sm" variant="light" color="orange">
                  Provisional
                </Badge>
              )}
            </Stack>
          </Group>
        </Paper>

        <Paper p="lg" className="glass">
          <Text fw={600} size="sm" c="cyan.3" mb="md" ff="Orbitron, sans-serif">
            Quick Actions
          </Text>
          <Group gap="sm">
            <Button
              leftSection={<IconPlayerPlay size={16} />}
              onClick={() => navigate('/')}
            >
              Browse Stories
            </Button>
            {!isProvisional && (
              <Button
                variant="light"
                color="cyan"
                leftSection={<IconUsers size={16} />}
                onClick={() => navigate('/teams')}
              >
                My Teams
              </Button>
            )}
            <Button
              variant="light"
              color="gray"
              leftSection={<IconSettings size={16} />}
              onClick={() => navigate('/settings')}
            >
              Settings
            </Button>
            <Button
              variant="subtle"
              color="red"
              onClick={logout}
            >
              Log Out
            </Button>
          </Group>
        </Paper>

        {/* Profile Analysis Link */}
        {!isProvisional && (
          <Paper
            p="lg"
            className="glass"
            style={{
              borderColor: 'rgba(0, 229, 255, 0.15)',
              cursor: 'pointer',
            }}
            onClick={() => navigate('/profile/analysis')}
          >
            <Group gap="md" wrap="nowrap">
              <IconBrain
                size={32}
                color="var(--mantine-color-cyan-4)"
                style={{ flexShrink: 0 }}
              />
              <Stack gap={2} style={{ flex: 1 }}>
                <Text fw={600} size="sm" c="cyan.3" ff="Orbitron, sans-serif">
                  Profile Analysis
                </Text>
                <Text size="xs" c="dimmed">
                  {completedSessions?.length >= 2
                    ? 'View your cross-session profile analysis'
                    : 'Complete more stories to unlock your profile analysis'}
                </Text>
              </Stack>
              <Button
                variant="light"
                color="cyan"
                size="sm"
                leftSection={<IconChartRadar size={14} />}
                disabled={!completedSessions || completedSessions.length < 1}
                onClick={(e) => {
                  e.stopPropagation()
                  navigate('/profile/analysis')
                }}
              >
                View
              </Button>
            </Group>
          </Paper>
        )}

        {/* Completed Sessions with Analysis Links */}
        {!isProvisional && (
          <Paper p="lg" className="glass">
            <Text fw={600} size="sm" c="cyan.3" mb="md" ff="Orbitron, sans-serif">
              Completed Stories
            </Text>
            {sessionsLoading ? (
              <Group gap="sm">
                <Loader size="xs" color="cyan" />
                <Text size="sm" c="dimmed">Loading sessions...</Text>
              </Group>
            ) : completedSessions?.length > 0 ? (
              <Stack gap="sm">
                {completedSessions.map((session) => (
                  <Stack key={session.id} gap={6}>
                    <Group justify="space-between" align="flex-start" wrap="nowrap">
                      <Stack gap={4} style={{ minWidth: 0 }}>
                        <Text size="sm" fw={600} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {session.story_name || `Session #${session.id}`}
                        </Text>
                        <Group gap={6} wrap="wrap">
                          {session.completed_at && (
                            <Text size="xs" c="dimmed">
                              {new Date(session.completed_at).toLocaleDateString()}
                            </Text>
                          )}
                          {/* Solo vs team play, mirroring the in-story MissionBar badges. */}
                          {session.team_id ? (
                            <Badge
                              size="xs"
                              variant="light"
                              color="teal"
                              leftSection={<IconUsers size={10} />}
                            >
                              {session.team_name ? `Team · ${session.team_name}` : 'Team'}
                            </Badge>
                          ) : (
                            <Badge size="xs" variant="outline" color="gray">
                              Solo
                            </Badge>
                          )}
                          {session.role && (
                            <Badge size="xs" variant="outline" color="violet">
                              {humanizeRole(session.role)}
                            </Badge>
                          )}
                        </Group>
                      </Stack>
                      <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
                        <Button
                          size="xs"
                          variant="light"
                          color="cyan"
                          leftSection={<IconMap2 size={14} />}
                          onClick={() => navigate(`/review/${session.id}`)}
                        >
                          Review
                        </Button>
                        <Button
                          size="xs"
                          variant="subtle"
                          color="cyan"
                          leftSection={<IconListDetails size={14} />}
                          onClick={() => navigate(`/summary/${session.id}`)}
                        >
                          Summary
                        </Button>
                        <Button
                          size="xs"
                          variant="subtle"
                          color="cyan"
                          leftSection={<IconChartRadar size={14} />}
                          onClick={() => navigate(`/analysis/${session.id}`)}
                        >
                          Analysis
                        </Button>
                      </Group>
                    </Group>
                  </Stack>
                ))}
              </Stack>
            ) : (
              <Text size="sm" c="dimmed">
                Complete a story to see your analysis here.
              </Text>
            )}
          </Paper>
        )}
      </Stack>
    </Container>
  )
}
